import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { Col, Row, Typography } from 'antd'
import { Icon } from '@iconify/react'

import { PRIVATE_ROUTES } from '../../routes'

import { loginState } from '../login/login.slice'
import {
    fetchHomePeople,
    fetchHomeProjects,
    fetchHomeTasks,
    homeState,
} from './home.slice'

import styles from './home.module.css'

import PeopleCard from './components/PeopleCard'
import MyTasksCard from './components/MyTasksCard'
import ProjectsCard from './components/ProjectsCard'

const { Text, Title } = Typography
const PROJECT_PAGE_LIMIT = 12
const OPEN_WEATHER_API_KEY =
    process.env.REACT_APP_OPENWEATHER_API_KEY ||
    '14df025c8c0c82978efcaa1be6b69d57'

const TASK_TAB_KEY_MAP = {
    Upcoming: 'upcoming',
    Overdue: 'overdue',
    Completed: 'completed',
}

const getGreeting = (date = new Date()) => {
    const hour = date.getHours()
    if (hour < 12) return 'Good morning'
    if (hour < 17) return 'Good afternoon'
    return 'Good evening'
}

const formatTopDate = (date = new Date()) => {
    try {
        return new Intl.DateTimeFormat(undefined, {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
        }).format(date)
    } catch {
        return date.toDateString()
    }
}

const getDisplayName = (userProfile) => {
    const raw =
        userProfile?.userName ||
        userProfile?.name ||
        userProfile?.fullName ||
        userProfile?.email
    if (!raw || typeof raw !== 'string') return 'there'
    const name = raw.trim()
    if (!name) return 'there'
    if (name.includes('@')) return name.split('@')[0]
    return name
}

const toFahrenheit = (celsius) => {
    if (typeof celsius !== 'number') return null
    return (celsius * 9) / 5 + 32
}

const pickWeatherIcon = (weatherMain) => {
    const key = String(weatherMain || '').toLowerCase()
    if (key.includes('clear')) return 'mdi:weather-sunny'
    if (key.includes('cloud')) return 'mdi:weather-cloudy'
    if (key.includes('rain') || key.includes('drizzle'))
        return 'mdi:weather-rainy'
    if (key.includes('thunder')) return 'mdi:weather-lightning'
    if (key.includes('snow')) return 'mdi:weather-snowy'
    if (key.includes('mist') || key.includes('fog') || key.includes('haze')) {
        return 'mdi:weather-fog'
    }
    return 'mdi:weather-cloudy'
}

const HomePage = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const [taskTab, setTaskTab] = useState('Upcoming')
    const { userProfile } = useSelector(loginState)
    const {
        projects,
        projectsStatus,
        projectsError,
        projectsTotal,
        projectsHasNext,
        people,
        peopleStatus,
        peopleError,
        peopleTotal,
        peopleHasNext,
        tasksByTab,
    } = useSelector(homeState)

    const now = useMemo(() => new Date(), [])
    const topDate = useMemo(() => formatTopDate(now), [now])
    const greeting = useMemo(() => getGreeting(now), [now])
    const displayName = useMemo(
        () => getDisplayName(userProfile),
        [userProfile]
    )
    const isClientUser = Boolean(
        userProfile?.isSupportUser && !userProfile?.isExistingUser
    )

    const [weatherStatus, setWeatherStatus] = useState('idle')
    const [weatherData, setWeatherData] = useState(null)
    const [tempUnit, setTempUnit] = useState('C')

    const getWeatherData = useCallback(async () => {
        if (!navigator?.geolocation) {
            setWeatherStatus('error')
            return
        }

        setWeatherStatus((s) => (s === 'loaded' ? 'loaded' : 'loading'))
        navigator.geolocation.getCurrentPosition(
            async (position) => {
                try {
                    const lat = position.coords.latitude
                    const lon = position.coords.longitude
                    const response = await fetch(
                        `https://api.openweathermap.org/data/2.5/weather?lat=${lat}&lon=${lon}&units=metric&appid=${OPEN_WEATHER_API_KEY}`
                    )
                    if (!response.ok) {
                        throw new Error(
                            `HTTP error! Status: ${response.status}`
                        )
                    }
                    const data = await response.json()
                    setWeatherData(data)
                    setWeatherStatus('loaded')
                } catch {
                    setWeatherStatus('error')
                }
            },
            (err) => {
                if (err?.code === 1) setWeatherStatus('denied')
                else setWeatherStatus('error')
            },
            {
                enableHighAccuracy: false,
                timeout: 10000,
                maximumAge: 10 * 60 * 1000,
            }
        )
    }, [])

    useEffect(() => {
        getWeatherData()
        const timer = setInterval(getWeatherData, 3600000)
        return () => clearInterval(timer)
    }, [getWeatherData])

    const tempC = weatherData?.main?.temp
    const tempF = useMemo(() => toFahrenheit(tempC), [tempC])
    const weatherMain = weatherData?.weather?.[0]?.main
    const weatherDesc = weatherData?.weather?.[0]?.description
    const city = weatherData?.name
    const humidity = weatherData?.main?.humidity
    const windKmh = useMemo(() => {
        const ms = weatherData?.wind?.speed
        if (typeof ms !== 'number') return null
        return Math.round(ms * 3.6)
    }, [weatherData?.wind?.speed])

    const selectedTaskTabKey = TASK_TAB_KEY_MAP[taskTab] || 'upcoming'
    const selectedTaskState = tasksByTab?.[selectedTaskTabKey] || {
        items: [],
        status: 'idle',
        error: '',
        total: 0,
        hasNext: true,
    }

    const fetchProjects = useCallback(
        (reset = false) => {
            dispatch(fetchHomeProjects({ reset, limit: PROJECT_PAGE_LIMIT }))
        },
        [dispatch]
    )
    const fetchPeople = useCallback(
        (reset = false) => {
            dispatch(fetchHomePeople({ reset, limit: PROJECT_PAGE_LIMIT }))
        },
        [dispatch]
    )
    const fetchTasks = useCallback(
        (tab, reset = false) => {
            dispatch(fetchHomeTasks({ tab, reset, limit: PROJECT_PAGE_LIMIT }))
        },
        [dispatch]
    )

    useEffect(() => {
        fetchProjects(true)
    }, [fetchProjects])
    useEffect(() => {
        if (!isClientUser) {
            fetchPeople(true)
        }
    }, [fetchPeople, isClientUser])
    useEffect(() => {
        fetchTasks(selectedTaskTabKey, true)
    }, [fetchTasks, selectedTaskTabKey])

    const openProjects = useCallback(() => {
        navigate(PRIVATE_ROUTES.projects.root)
    }, [navigate])

    return (
        <div className={styles.page}>
            <div className={styles.topBar}>
                <div>
                    <Text className={styles.topDate}>{topDate}</Text>
                    <Title level={2} className={styles.greeting}>
                        {greeting}, {displayName}!
                    </Title>
                </div>

                <div className={styles.weather}>
                    <div className={styles.weatherIcon}>
                        <Icon
                            icon={pickWeatherIcon(weatherMain)}
                            className={styles.weatherIconGlyph}
                        />
                    </div>

                    <div className={styles.weatherMain}>
                        <div className={styles.weatherTempRow}>
                            <Text className={styles.weatherValue}>
                                {weatherStatus === 'loading' && '—'}
                                {weatherStatus === 'loaded' &&
                                    (tempUnit === 'C'
                                        ? Math.round(tempC)
                                        : Math.round(tempF))}
                                {(weatherStatus === 'error' ||
                                    weatherStatus === 'denied') &&
                                    '—'}
                            </Text>
                            <div className={styles.unitToggle}>
                                <button
                                    type="button"
                                    className={`${styles.unitButton} ${
                                        tempUnit === 'C'
                                            ? styles.unitActive
                                            : ''
                                    }`}
                                    onClick={() => setTempUnit('C')}
                                    aria-label="Show Celsius"
                                >
                                    °C
                                </button>
                                <span className={styles.unitDivider}>|</span>
                                <button
                                    type="button"
                                    className={`${styles.unitButton} ${
                                        tempUnit === 'F'
                                            ? styles.unitActive
                                            : ''
                                    }`}
                                    onClick={() => setTempUnit('F')}
                                    aria-label="Show Fahrenheit"
                                >
                                    °F
                                </button>
                            </div>
                        </div>
                        <div className={styles.weatherSubRow}>
                            <Text className={styles.weatherLocation}>
                                {city || 'Your location'}
                            </Text>
                            <Text className={styles.weatherDot}>•</Text>
                            <Text className={styles.weatherCondition}>
                                {weatherStatus === 'loaded'
                                    ? weatherDesc || weatherMain
                                    : weatherStatus === 'denied'
                                      ? 'Location blocked'
                                      : weatherStatus === 'error'
                                        ? 'Weather unavailable'
                                        : 'Loading...'}
                            </Text>
                        </div>
                    </div>
                    <div className={styles.weatherMeta}>
                        <Text className={styles.weatherMetaText}>
                            Humidity:{' '}
                            {typeof humidity === 'number'
                                ? `${humidity}%`
                                : '—'}
                        </Text>
                        <Text className={styles.weatherMetaText}>
                            Wind:{' '}
                            {typeof windKmh === 'number'
                                ? `${windKmh} km/h`
                                : '—'}
                        </Text>
                    </div>
                </div>
            </div>

            <Row gutter={[16, 16]}>
                <Col xs={24} lg={24}>
                    <MyTasksCard
                        taskTab={taskTab}
                        setTaskTab={setTaskTab}
                        tasksByTab={tasksByTab}
                        selectedTaskState={selectedTaskState}
                        onRetry={() => fetchTasks(selectedTaskTabKey, true)}
                        onLoadMore={(tab) => fetchTasks(tab, false)}
                        onTaskDatesUpdated={() =>
                            fetchTasks(selectedTaskTabKey, true)
                        }
                        onOpenTask={(task) => {
                            if (!task?._id) return
                            navigate(
                                PRIVATE_ROUTES.projects.projectIdViewTask(
                                    task?.projectId,
                                    'board',
                                    task?._id
                                )
                            )
                        }}
                    />
                </Col>
                <Col xs={24} lg={isClientUser ? 24 : 12}>
                    <ProjectsCard
                        projects={projects}
                        projectsStatus={projectsStatus}
                        projectsError={projectsError}
                        projectsTotal={projectsTotal}
                        projectsHasNext={projectsHasNext}
                        onRetry={() => fetchProjects(true)}
                        onLoadMore={() => fetchProjects(false)}
                        onOpenProjects={openProjects}
                        onOpenProject={(project) => {
                            if (!project?._id) return openProjects()
                            navigate(
                                PRIVATE_ROUTES.projects.projectId(project._id)
                            )
                        }}
                        onProjectUpdated={() => fetchProjects(true)}
                        title="Projects"
                        rightLabel="Recents"
                    />
                </Col>
                {!isClientUser ? (
                    <Col xs={24} lg={12}>
                        <PeopleCard
                            people={people}
                            peopleStatus={peopleStatus}
                            peopleError={peopleError}
                            peopleTotal={peopleTotal}
                            peopleHasNext={peopleHasNext}
                            onRetry={() => fetchPeople(true)}
                            onLoadMore={() => fetchPeople(false)}
                        />
                    </Col>
                ) : null}
            </Row>
        </div>
    )
}

export default HomePage
